import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ReferenceCard } from '../../../shared/ui/components/ReferenceCard';
import { TagChip } from '../../../shared/ui/components/TagChip';
import { SimpleTagChip } from '../../../shared/ui/components/SimpleTagChip';
import { useSearch } from '../../../shared/contexts/SearchContext';
import { useReferenceSharing } from '../../../hooks/useReferenceSharing';
import { useReferenceSelection } from '../../../shared/contexts/ReferenceSelectionContext';
import type { Doc } from '../../../../convex/_generated/dataModel';

type ReferenceDoc = Doc<'references'> & {
  imageUrl?: string | null;
  sourceURL?: string | null;
  tags: Doc<'tags'>[];
  mediaItems: (Doc<'mediaItems'> & {
    url: string | null;
    mediaType: 'image' | 'video';
  })[];
  app?: Doc<'apps'> | null;
};

interface ViewProps {
  references: ReferenceDoc[];
  onEditReference?: (reference: ReferenceDoc) => void;
  onDelete: (reference: ReferenceDoc) => void;
  onAIPrompt: (reference: ReferenceDoc) => void;
  onTagClick: (tagId: string) => void;
  generateReferenceUrl: (referenceId: string) => string;
}

// Helper function to safely extract hostname from a URL
const getHostnameFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    try {
      const cleanUrl = url.replace(/^https?:\/\//, '');
      const domain = cleanUrl.split(/[/?#]/)[0];
      if (domain && domain.includes('.')) {
        return domain;
      }
    } catch {
      // If all fails, return null
    }
    return null;
  }
};

// Copy Icon Component
const CopyIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

// Share Icon Component
const ShareIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
  </svg>
);

/**
 * Grid View Component - Regular grid layout
 */
export function GridView({
  references,
  onEditReference,
  onDelete,
  onAIPrompt,
  onTagClick,
  generateReferenceUrl
}: ViewProps) {
  const { copyReferenceImage, copyReferenceLink } = useReferenceSharing();
  const { toggleSelection, isSelected } = useReferenceSelection();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 px-1 sm:px-0">
      {references.map((ref) => (
        <div key={ref._id} className="relative group">
          {/* Selection Checkbox */}
          <div className="absolute top-3 left-3 z-20">
            <label className="cursor-pointer">
              <input
                type="checkbox"
                checked={isSelected(ref._id)}
                onChange={() => toggleSelection(ref._id)}
                className="checkbox checkbox-lg border-2 border-gray-700 bg-white/90 hover:bg-white checked:bg-yellow-400 checked:border-yellow-500"
                onClick={(e) => e.stopPropagation()}
              />
            </label>
          </div>

          <ReferenceCard
            reference={ref}
            onEdit={onEditReference}
            onDelete={onDelete}
            onAIPrompt={onAIPrompt}
            onTagClick={onTagClick}
            generateReferenceUrl={generateReferenceUrl}
            viewMode="grid"
            onCopyImage={() => copyReferenceImage(ref.mediaItems?.[0]?.url || ref.imageUrl)}
            onCopyLink={() => copyReferenceLink(ref._id)}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Masonry View Component - Pinterest-style layout with varying heights
 */
export function MasonryView({
  references,
  onEditReference,
  onDelete,
  onAIPrompt,
  onTagClick,
  generateReferenceUrl
}: ViewProps) {
  const navigate = useNavigate();
  const { filters, filterState, filterDispatch, hasActiveFilters: hasNewFilters } = useSearch();
  const { copyReferenceImage, copyReferenceLink } = useReferenceSharing();
  const { toggleSelection, isSelected } = useReferenceSelection();

  return (
    <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-3 sm:gap-4 md:gap-6 px-1 sm:px-0">
      {references.map((ref) => (
        <div
          key={ref._id}
          className="break-inside-avoid mb-3 sm:mb-4 md:mb-6"
        >
          <ReferenceCard
            reference={ref}
            onEdit={onEditReference}
            onDelete={onDelete}
            onAIPrompt={onAIPrompt}
            onTagClick={onTagClick}
            generateReferenceUrl={generateReferenceUrl}
            viewMode="masonry"
            onCopyImage={() => copyReferenceImage(ref.mediaItems?.[0]?.url || ref.imageUrl)}
            onCopyLink={() => copyReferenceLink(ref._id)}
            showSelection={true}
            isSelected={isSelected(ref._id)}
            onToggleSelection={toggleSelection}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * List View Component - Detailed list layout
 */
export function ListView({
  references,
  onEditReference,
  onDelete,
  onAIPrompt,
  onTagClick,
  generateReferenceUrl
}: ViewProps) {
  const navigate = useNavigate();
  const { filters, filterState, filterDispatch, hasActiveFilters: hasNewFilters } = useSearch();
  const { copyReferenceImage, copyReferenceLink } = useReferenceSharing();
  const { toggleSelection, isSelected } = useReferenceSelection();

  return (
    <div className="card bg-base-100 shadow-lg border-2 border-base-300 divide-y divide-base-300 mx-1 sm:mx-0">
      {references.map((ref) => (
        <div
          key={ref._id}
          className="p-4 hover:bg-base-200 transition-colors"
        >
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            {/* Checkbox */}
            <div className="flex-shrink-0 self-start">
              <label className="cursor-pointer">
                <input
                  type="checkbox"
                  checked={isSelected(ref._id)}
                  onChange={() => toggleSelection(ref._id)}
                  className="checkbox border-2 border-gray-700 checked:bg-yellow-400 checked:border-yellow-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </label>
            </div>

            {/* Thumbnail */}
            <div
              className="flex-shrink-0 cursor-pointer self-start"
              onClick={() => void navigate(generateReferenceUrl(ref._id))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  void navigate(generateReferenceUrl(ref._id));
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`View reference: ${ref.title}`}
            >
              <div className="w-16 h-16 bg-base-200 rounded-md overflow-hidden">
                {ref.mediaItems?.[0]?.url ? (
                  ref.mediaItems[0].mediaType === 'video' ? (
                    <div className="relative w-full h-full">
                      <video
                        src={ref.mediaItems[0].url}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                        poster=""
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                        <div className="bg-base-100 bg-opacity-90 rounded-full p-1">
                          <svg className="w-4 h-4 text-base-content" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <title>Play video</title>
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={ref.mediaItems[0].url}
                      alt={ref.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )
                ) : ref.imageUrl ? (
                  <img
                    src={ref.imageUrl}
                    alt={ref.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-base-content/40">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <title>No media</title>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-base-content mb-1">{ref.title}</h3>
              {ref.description && (
                <p className="text-sm text-base-content/70 mb-2 line-clamp-2">{ref.description}</p>
              )}

              {/* Tags */}
              {ref.tags && ref.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {ref.tags.map((tag) => {
                    const isActiveInNewSystem = Object.values(filterState.tags).some(filterTag => filterTag.value === tag.name);
                    const activeTagMode = Object.values(filterState.tags).find(filterTag => filterTag.value === tag.name)?.mode;
                    const isActiveInLegacy = filters.tags.some((filterTag: any) => filterTag.id === tag._id && !filterTag.negative);
                    
                    return (
                      <div
                        key={tag._id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (hasNewFilters) {
                            const tagId = `${activeTagMode || 'inclusive'}-${tag.name}`;
                            filterDispatch({
                              type: 'TOGGLE_TAG_MODE',
                              tagId,
                              value: tag.name,
                              category: undefined
                            });
                          } else {
                            onTagClick(tag._id);
                          }
                        }}
                      >
                        {hasNewFilters && isActiveInNewSystem ? (
                          <SimpleTagChip
                            tag={tag.name}
                            mode={activeTagMode!}
                            onClick={() => {
                              const tagId = `${activeTagMode}-${tag.name}`;
                              filterDispatch({
                                type: 'TOGGLE_TAG_MODE',
                                tagId,
                                value: tag.name,
                                category: undefined
                              });
                            }}
                            size="sm"
                          />
                        ) : (
                          <TagChip
                            tag={tag}
                            onClick={(tagId) => {
                              if (hasNewFilters) {
                                filterDispatch({
                                  type: 'TOGGLE_TAG_MODE',
                                  tagId: `inclusive-${tag.name}`,
                                  value: tag.name,
                                  category: undefined
                                });
                              } else {
                                onTagClick(tagId);
                              }
                            }}
                            variant="default"
                            size="sm"
                            isActive={isActiveInLegacy}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* URL */}
              {ref.sourceURL && (
                <div className="text-xs text-base-content/50 truncate mb-2 sm:mb-0">
                  {getHostnameFromUrl(ref.sourceURL)}
                </div>
              )}

              {/* Action Buttons - Mobile */}
              <div className="mt-3 pt-3 border-t border-base-300 flex justify-end space-x-1 sm:hidden">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    void navigate(generateReferenceUrl(ref._id));
                  }}
                  type="button"
                  aria-label={`View ${ref.title}`}
                  className="btn btn-sm btn-outline"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    void copyReferenceImage(ref.mediaItems?.[0]?.url || ref.imageUrl);
                  }}
                  type="button"
                  aria-label="Copy image to clipboard"
                  className="btn btn-sm btn-outline"
                  title="Copy image"
                >
                  <CopyIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    void copyReferenceLink(ref._id);
                  }}
                  type="button"
                  aria-label="Copy reference link"
                  className="btn btn-sm btn-outline"
                  title="Copy link"
                >
                  <ShareIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAIPrompt(ref);
                  }}
                  type="button"
                  aria-label={`Generate AI prompt for ${ref.title}`}
                  className="btn btn-sm btn-secondary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Action Buttons - Desktop */}
            <div className="hidden sm:flex flex-shrink-0 space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void navigate(generateReferenceUrl(ref._id));
                }}
                type="button"
                aria-label={`View ${ref.title}`}
                className="btn btn-sm btn-outline"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void copyReferenceImage(ref.mediaItems?.[0]?.url || ref.imageUrl);
                }}
                type="button"
                aria-label="Copy image to clipboard"
                className="btn btn-sm btn-outline"
                title="Copy image"
              >
                <CopyIcon className="h-4 w-4 mr-1" />
                Copy
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void copyReferenceLink(ref._id);
                }}
                type="button"
                aria-label="Copy reference link"
                className="btn btn-sm btn-outline"
                title="Copy link"
              >
                <ShareIcon className="h-4 w-4 mr-1" />
                Link
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAIPrompt(ref);
                }}
                type="button"
                aria-label={`Generate AI prompt for ${ref.title}`}
                className="btn btn-sm btn-secondary"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </button>
              {onEditReference && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditReference(ref);
                  }}
                  type="button"
                  aria-label={`Edit ${ref.title}`}
                  className="btn btn-sm btn-info"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
              )}
              <button
                onClick={() => void onDelete(ref)}
                className="btn btn-error btn-sm"
                title="Delete reference"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
} 